import React, { useContext, useEffect, useMemo, useState } from 'react';

import MapBoxMap from '@totem/components/common/mapBoxMap/MapBoxMap';
import buildingMarker from '@totem/components/common/svgIcons/buildingMarker.svg';
import SummaryContext from '@totem/components/portfolio/overview/summary/summaryContext';
import { Building } from '@totem/components/portfolio/overview/summary/types';
import { isNotNull } from '@totem/utilities/common';

const styles = {
  zoom: 2,
  markerSize: 32,
};

const SummaryMapBoxMap = () => {
  const { filteredRegions } = useContext(SummaryContext);
  const [buildings, setBuildings] = useState<Building[]>([]);

  useEffect(() => {
    if (isNotNull(filteredRegions)) {
      const buildingArray: Building[] = [];
      for (let idx = 0; idx < filteredRegions.length; idx++) {
        buildingArray.push(...filteredRegions[idx].buildings);
      }
      setBuildings(buildingArray);
    }
  }, [filteredRegions]);

  const buildingMarkers = useMemo(() => {
    if (isNotNull(buildings)) {
      return buildings
        .filter(({ location }) => location.lat !== 0 || location.lng !== 0)
        .map(({ id, location }) => ({
          id,
          lng: location.lng,
          lat: location.lat,
          iconUrl: buildingMarker,
        }));
    }
    return [];
  }, [buildings]);

  const mapConfig = useMemo((): {
    center: [number, number];
    zoom: number;
    bounds?: [[number, number], [number, number]];
  } => {
    const valid = buildings.filter(
      ({ location }) => location.lat !== 0 || location.lng !== 0,
    );

    if (valid.length === 0) {
      return { center: [-98.5795, 39.8282], zoom: 2 };
    }

    const lats = valid.map(({ location }) => location.lat);
    const lngs = valid.map(({ location }) => location.lng);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const center: [number, number] = [
      (minLng + maxLng) / 2,
      (minLat + maxLat) / 2,
    ];

    if (minLat === maxLat && minLng === maxLng) {
      return { center, zoom: 13 };
    }

    return {
      center,
      zoom: 2,
      bounds: [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
    };
  }, [buildings]);

  return (
    <MapBoxMap
      markers={buildingMarkers}
      zoom={mapConfig.zoom}
      center={mapConfig.center}
      bounds={mapConfig.bounds}
      markerSize={styles.markerSize}
    />
  );
};

export default SummaryMapBoxMap;
